import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// From https://overreacted.io/making-setinterval-declarative-with-react-hooks/
export const useInterval = (callback: () => void, delay: number | null): void => {
	const savedCallback = useRef<(() => void) | undefined>(undefined);

	useEffect(() => {
		savedCallback.current = callback;
	}, [callback]);

	useEffect(() => {
		const tick = (): void => {
			savedCallback.current?.();
		};
		if (delay !== null) {
			const id = setInterval(tick, delay);
			return () => clearInterval(id);
		}
	}, [delay]);
};

export const usePrevious = <T>(value: T): T | undefined => {
	const ref = useRef<T>();
	useEffect(() => {
		ref.current = value;
	}, [value]);
	return ref.current;
};

export const useOnChange = <T>(
	value: T,
	callback: (previousValue: T | undefined) => void,
): void => {
	const previousValue = usePrevious(value);
	if (previousValue !== value) {
		callback(previousValue);
	}
};

export const useOnMount = (callback: () => void): void => {
	useEffect(() => {
		callback();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
};

export const useOnUnMount = (callback: () => void): void => {
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => callback, []);
};

// From https://usehooks.com/useDebounce/
export const useDebounce = <T>(value: T, delay = 300): T => {
	const [debouncedValue, setDebouncedValue] = useState(value);

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedValue(value);
		}, delay);

		return () => {
			clearTimeout(handler);
		};
	}, [value, delay]);

	return debouncedValue;
};

export const useScrollTop = (x = 0, y = 0): void => {
	useEffect(() => {
		window.scrollTo && window.scrollTo(x, y);
	}, [x, y]);
};

type UseModalResult = {
	isOpen: boolean;
	open: () => void;
	close: () => void;
};

export const useModal = (): UseModalResult => {
	const [isOpen, setIsOpen] = useState(false);
	const open = (): void => setIsOpen(true);
	const close = (): void => setIsOpen(false);

	return { isOpen, open, close };
};

export const useQueryParams = (): Record<string, string> => {
	const search = useLocation().search;
	const urlSearchParams = new URLSearchParams(search);
	const params = Object.fromEntries(urlSearchParams.entries());
	return params;
};
